import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useMemo } from "react";
import {
  Accordion,
  ActionIcon,
  Alert,
  Anchor,
  CopyButton,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { MantineReactTable, useMantineReactTable } from "mantine-react-table";
import { Title, Text, Container, Flex, Box, Image } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconAlertSquareRounded,
  IconCheck,
  IconCopy,
  IconMinus,
  IconPlus,
} from "@tabler/icons-react";
import { formatHexStr } from "../utils/formatHexStr";
import { useTableIcons } from "../utils/collectionFound/iconsUtil";
import { downloadJsonToCsvMissing } from "../utils/collectionFound/downloadJsonToCsvMissing";
import { downloadJsonToCsvRecords } from "../utils/collectionFound/downloadJsonToCsvRecords";
import {
  useTableData,
  useTableColumns,
  useTableOptions,
  maxTimeStampDiff,
} from "../utils/collectionFound/matchedDataTable";

import {
  useDataToleranceNew,
  useColumnsToleranceNew,
  useTableToleranceNew,
} from "../utils/collectionFound/toleranceDataTableNew";
import CustomTooltip from "./mantineCustomComponents/CustomTooltip";
import { useStylesTooltip } from "../utils/mantine/useStylesTooltip";
import CustomAnchor from "./mantineCustomComponents/CustomAnchor";
import { useStylesPage } from "../utils/mantine/useStylesPage";
import { useStylesTechnicalDetail } from "../utils/mantine/useStylesTechnicalDetail";
import { userFoundDataForCollectionFound } from "../utils/apis";
import {
  formatTimeStamp,
  LONG_DISPLAY_FORMAT,
} from "../utils/collectionFound/dateTimeUtils";
import StampDetailCollectionFound from "./StampDetailCollectionFound";
import { useStylesAccordion } from "../utils/mantine/useStylesAccordion";
import { useStylesTable } from "../utils/mantine/useStylesTable";
import { useStylesIcons } from "../utils/mantine/useStylesIcons";

const normalizeDisplayTimestamp = (timestamp) => {
  if (timestamp == null) {
    return timestamp;
  }

  let res = timestamp;
  if (typeof timestamp === "number") {
    res = timestamp < 1000000000000 ? timestamp * 1000 : timestamp;
  } else if (typeof timestamp === "string") {
    const numericTimestamp = Number(timestamp);

    if (!Number.isNaN(numericTimestamp)) {
      res =
        numericTimestamp < 1000000000000
          ? numericTimestamp * 1000
          : numericTimestamp;
    }
  }

  return new Date(res).toISOString();
};

const CollectionFound = ({
  contentCollectionFound,
  staticBasePath,
  fileName,
  fileType,
}) => {
  const { classes } = useStylesTooltip();
  const { classes: classesPage } = useStylesPage();
  const { classes: classesTechnicalDetail } = useStylesTechnicalDetail();
  const { classes: classesAccordion } = useStylesAccordion();
  const { classes: tableClasses } = useStylesTable();
  const { classes: classesIcons } = useStylesIcons();
  // Maximum timestamp deviation from global record
  const resultMaxTimeStampDiff = maxTimeStampDiff(contentCollectionFound);

  // Mantine theme instance to apply custom styles and theming throughout the component
  const theme = useMantineTheme();

  // Media query to detect if the screen width is less than 64em (1024px)
  const tabletMediaQuery = useMediaQuery("(max-width: 64em)");

  // accordion
  const [opened, setOpened] = useState(null);

  // Icons used within the tables, provided through a custom utility function
  const faIcons = useMemo(() => useTableIcons(), []);

  // Data preparation for the main table using memoized custom utility function
  const data = useMemo(
    () =>
      useTableData(
        contentCollectionFound,
        staticBasePath,
        fileType,
        classesIcons,
        theme,
      ),
    [contentCollectionFound.missing_in_csv, staticBasePath, fileType],
  );

  const timeZone = contentCollectionFound?.collection_info?.display_timezone;
  const asOfDisplayTimestamp =
    contentCollectionFound?.collection_info?.as_of_timestamp ??
    contentCollectionFound?.collection_info?.data_freshness_timestamp;
  // Column definitions for the main table using a utility function
  const columns = useMemo(
    () => useTableColumns(fileType, timeZone, contentCollectionFound),
    [fileType, timeZone, contentCollectionFound],
  );

  // Configuration options for the main table using Mantine's table library
  const table = useMantineReactTable(
    useTableOptions(columns, data, faIcons, theme),
  );

  // Data preparation for the tolerance table using a custom utility function
  const dataToleranceNew = useMemo(
    () =>
      useDataToleranceNew(
        contentCollectionFound.time_buckets,
        contentCollectionFound.matched_records,
      ),
    [
      contentCollectionFound.time_buckets,
      contentCollectionFound.matched_records,
    ],
  );

  // Column definitions for the tolerance table using a utility function
  const columnsToleranceNew = useMemo(
    () => useColumnsToleranceNew(theme, tableClasses),
    [theme, tableClasses],
  );

  // Configuration options for the tolerance table
  const tableToleranceNew = useMantineReactTable(
    useTableToleranceNew(
      columnsToleranceNew,
      dataToleranceNew,
      theme,
      tableClasses,
    ),
  );

  // Data for csv matched
  const combinedData = contentCollectionFound?.matched_records?.map(
    (record) => ({
      ...record,
      ...contentCollectionFound.collection_info,
    }),
  );

  const [totalCollections, setTotalCollections] = useState(null);
  const [firstStampInCollection, setFirstStampInCollection] = useState(null);
  const [lastStampInCollection, setLastStampInCollection] = useState(null);
  const [totalStampInCollection, setTotalStampInCollection] = useState(null);
  const [userVerifiedInfo, setUserVerifiedInfo] = useState({
    is_verified: false,
    verified_since: null,
  });

  useEffect(() => {
    const fetchUserFoundDataForCollectionFound = async () => {
      try {
        const userAddress =
          contentCollectionFound?.collection_info?.user_address;
        const collectionHash =
          contentCollectionFound?.collection_info?.collection_id;

        if (!userAddress) {
          console.warn("User address is missing. Skipping data fetch.");
          return;
        }

        await userFoundDataForCollectionFound(
          setTotalCollections,
          setFirstStampInCollection,
          setLastStampInCollection,
          setTotalStampInCollection,
          userAddress,
          collectionHash,
          setUserVerifiedInfo,
        );
      } catch (error) {
        console.error("Failed to fetch user or collection data:", error);
      }
    };

    fetchUserFoundDataForCollectionFound();
  }, [contentCollectionFound]);

  return (
    <>
      <Flex
        align={theme.flex.align.center}
        direction="column"
        className={classesPage.pageCollectionFound}
      >
        <Box className="header-wrap">
          <Container
            size="sm"
            pt={theme.padding.xxxlbEm}
            pb={theme.padding.xxxlEm}
          >
            <Flex
              justify={theme.flex.justify.center}
              direction="column"
              align="center"
            >
              {contentCollectionFound.missing_in_blockchain.length === 0 &&
              contentCollectionFound.missing_in_csv.length === 0 ? (
                <>
                  <Image
                    src={`${staticBasePath}public/images/svg/vb-icon-verified.svg`}
                    alt="Submitted Data Validated!"
                    className={classesPage.pageHeaderIcon}
                  />
                  {asOfDisplayTimestamp != null && (
                    <Flex
                      gap={theme.flex.gap.xs}
                      align={theme.flex.align.center}
                      justify={theme.flex.justify.center}
                    >
                      As of{" "}
                      {formatTimeStamp(
                        normalizeDisplayTimestamp(asOfDisplayTimestamp),
                        timeZone,
                        LONG_DISPLAY_FORMAT,
                      )}
                    </Flex>
                  )}
                  {contentCollectionFound?.collection_info
                    ?.behind_blockchain_records != null &&
                    contentCollectionFound.collection_info
                      .behind_blockchain_records !== 0 && (
                      <Flex
                        gap={theme.flex.gap.xs}
                        align={theme.flex.align.center}
                        justify={theme.flex.justify.center}
                      >
                        The provided collection is{" "}
                        {
                          contentCollectionFound.collection_info
                            .behind_blockchain_records
                        }{" "}
                        {contentCollectionFound.collection_info
                          .behind_blockchain_records === 1
                          ? "element"
                          : "elements"}{" "}
                        behind the corresponding collection on the chain.
                      </Flex>
                    )}
                  <Flex
                    gap={theme.flex.gap.xs}
                    align={theme.flex.align.center}
                    justify={theme.flex.justify.center}
                  >
                    <Title order={1}>Collection Verified!</Title>
                  </Flex>
                  {resultMaxTimeStampDiff && (
                    <>
                      <Flex
                        gap={theme.flex.gap.small}
                        align={theme.flex.align.center}
                        justify={theme.flex.justify.center}
                      >
                        <Image
                          w={19}
                          src={`${staticBasePath}public/images/svg/vb-icon-verify.svg`}
                          alt=""
                        />
                        <Text
                          size="xl"
                          classNames={{
                            root: "match-to-blockchain-data",
                          }}
                          styles={(theme) => ({
                            root: {
                              fontFamily: theme.headings.fontFamily.bold,
                            },
                          })}
                        >
                          {" "}
                          100% Match to Blockchain Data
                        </Text>
                      </Flex>
                    </>
                  )}
                </>
              ) : (
                <>
                  <Image
                    src={`${staticBasePath}public/images/svg/not_validate.svg`}
                    alt="Data Cannot be Verified"
                    className={classesPage.pageHeaderIcon}
                    style={{
                      marginTop: theme.margin.none,
                    }}
                  />
                  <Flex
                    gap={theme.flex.gap.xs}
                    align={theme.flex.align.center}
                    justify={theme.flex.justify.center}
                  >
                    <Title order={1}>Data Cannot be Verified</Title>

                    <CustomTooltip
                      content={
                        <>
                          <Text
                            classNames={{
                              root: classes.tooltipText,
                            }}
                          >
                            vBase Verify is unable to verify your Submitted Data
                            vs. the vBase Stamps in the specified Blockchain
                            Collection. Use the details below to find which
                            records/stamps are missing. To read more on how
                            collection verification works,{" "}
                            <Anchor
                              underline="always"
                              c={theme.customColors.blackLight}
                              href="https://docs.vbase.com/"
                              target="_blank"
                            >
                              visit our docs
                            </Anchor>
                            .
                          </Text>
                        </>
                      }
                    ></CustomTooltip>
                  </Flex>
                  <Alert
                    variant="light"
                    className="alert"
                    color="black"
                    title="Stamps created in the last 10 seconds may not be visible yet."
                    icon={<IconAlertSquareRounded />}
                  ></Alert>
                  {asOfDisplayTimestamp != null && (
                    <Flex
                      gap={theme.flex.gap.xs}
                      align={theme.flex.align.center}
                      justify={theme.flex.justify.center}
                    >
                      As of{" "}
                      {formatTimeStamp(
                        normalizeDisplayTimestamp(asOfDisplayTimestamp),
                        timeZone,
                        LONG_DISPLAY_FORMAT,
                      )}
                    </Flex>
                  )}
                  {contentCollectionFound?.collection_info
                    ?.behind_blockchain_records != null &&
                    contentCollectionFound.collection_info
                      .behind_blockchain_records !== 0 && (
                      <Flex
                        gap={theme.flex.gap.xs}
                        align={theme.flex.align.center}
                        justify={theme.flex.justify.center}
                      >
                        The provided collection is{" "}
                        {
                          contentCollectionFound.collection_info
                            .behind_blockchain_records
                        }{" "}
                        {contentCollectionFound.collection_info
                          .behind_blockchain_records === 1
                          ? "element"
                          : "elements"}{" "}
                        behind the corresponding collection on the chain.
                      </Flex>
                    )}
                  {contentCollectionFound?.missing_in_csv.length > 0 && (
                    <Flex
                      pt={theme.padding.xs}
                      gap={theme.flex.gap.xs}
                      align={theme.flex.align.center}
                      justify={theme.flex.justify.center}
                    >
                      <Image
                        src={`${staticBasePath}public/images/svg/red_alert_clamation_icon.svg`}
                        alt="Data Cannot be Verified"
                        w={20}
                        h={20}
                        style={{
                          marginTop: theme.margin.none,
                          marginBottom: theme.margin.none,
                        }}
                      />
                      <Text
                        size="lg"
                        classNames={{
                          root: "missing-in-csv",
                        }}
                      >
                        {contentCollectionFound?.missing_in_csv.length} MISSING
                        Record(s) in Submitted Data vs. Blockchain Collection
                      </Text>
                    </Flex>
                  )}

                  {contentCollectionFound?.missing_in_blockchain.length > 0 && (
                    <Flex
                      gap={theme.flex.gap.xs}
                      align={theme.flex.align.center}
                      justify={theme.flex.justify.center}
                    >
                      <Image
                        src={`${staticBasePath}public/images/svg/red_alert_clamation_icon.svg`}
                        alt="Data Cannot be Verified"
                        w={20}
                        h={20}
                        style={{
                          marginTop: theme.margin.none,
                          marginBottom: theme.margin.none,
                        }}
                      />
                      <Text
                        size="lg"
                        classNames={{
                          root: "missing-in-blockchain",
                        }}
                      >
                        {contentCollectionFound?.missing_in_blockchain.length}{" "}
                        EXTRA Record(s) in Submitted Data vs. Blockchain
                        Collection
                      </Text>
                    </Flex>
                  )}
                </>
              )}
            </Flex>

            {contentCollectionFound.missing_in_blockchain.length === 0 &&
              contentCollectionFound.missing_in_csv.length === 0 &&
              resultMaxTimeStampDiff && (
                <Box mt={theme.margin.xsEm}>
                  {resultMaxTimeStampDiff !== "0s" && (
                    <Flex
                      gap={theme.flex.gap.small}
                      align={theme.flex.align.center}
                      justify={theme.flex.justify.center}
                      styles={() => ({
                        root: {
                          paddingLeft: theme.padding.smEm,
                          paddingRight: theme.padding.smEm,
                        },
                      })}
                    >
                      {" "}
                      <Image
                        w={19}
                        src={`${staticBasePath}public/images/svg/info_found.svg`}
                        alt=""
                      />{" "}
                      <Text
                        size="xl"
                        classNames={{
                          root: `value value-timestamps ${classesPage.timestampsMatch}`,
                        }}
                      >
                        Timestamps Match within {resultMaxTimeStampDiff}{" "}
                        <Anchor
                          size="xs"
                          underline="always"
                          c={theme.customColors.blackLight}
                          onClick={() => setOpened("technical")}
                          href="#container-difference"
                        >
                          (Details Below)
                        </Anchor>
                      </Text>
                    </Flex>
                  )}
                </Box>
              )}
          </Container>
        </Box>

        <Container
          styles={() => ({
            root: {
              width: "100%",
            },
          })}
          size="lg"
          pl={theme.padding.none}
          pr={theme.padding.none}
        >
          <StampDetailCollectionFound
            data={contentCollectionFound?.collection_info}
            verified={userVerifiedInfo}
            staticBasePath={staticBasePath}
          ></StampDetailCollectionFound>

          <Box
            maw="none"
            pl={theme.padding.none}
            pr={theme.padding.none}
            pt={theme.padding.xxlEm}
            className={`${classesTechnicalDetail.technicalBlock} ${classesTechnicalDetail.technical}`}
          >
            <Accordion
              className={classesAccordion.accordion}
              value={opened}
              onChange={setOpened}
              variant="separated"
            >
              <Accordion.Item value="technical">
                <Accordion.Control
                  chevron={
                    opened === "technical" ? <IconMinus /> : <IconPlus />
                  }
                >
                  <Flex gap={theme.flex.gap.xs}>
                    <Text className="title">Technical Details</Text>
                    {opened === "technical" ? (
                      <Text className="show-hide">Hide</Text>
                    ) : (
                      <>
                        <Box className="show-hide-box"></Box>
                        <Text className="show-hide hide">Show</Text>
                      </>
                    )}
                  </Flex>
                </Accordion.Control>
                <Accordion.Panel>
                  <Box>
                    {fileName && (
                      <Flex
                        gap={theme.flex.gap.xxs}
                        justify={theme.flex.justify.between}
                        className="item"
                        align={theme.flex.align.end}
                      >
                        <Text className="key">Source Data</Text>
                        <Box className="dots"></Box>
                        <Text
                          title={fileName}
                          className="value value-without-copy"
                        >
                          {formatHexStr(fileName, 35, 25, 63)}
                        </Text>
                      </Flex>
                    )}
                    <Flex
                      gap={theme.flex.gap.xxs}
                      justify={theme.flex.justify.between}
                      className="item"
                      align={theme.flex.align.end}
                    >
                      <Text className="key">Collection ID</Text>
                      <Box className="dots"></Box>
                      <Text className="value">
                        <CustomAnchor
                          text={formatHexStr(
                            contentCollectionFound.collection_info
                              ?.collection_id,
                          )}
                          componentClasses="link"
                          color={theme.customColors.black}
                          href={`/verify/user-data/?user=${
                            contentCollectionFound?.collection_info
                              ?.user_name ||
                            contentCollectionFound?.collection_info
                              ?.user_address
                          }&collection_cid=${
                            contentCollectionFound?.collection_info
                              ?.collection_id
                          }`}
                        />
                        <CopyButton
                          value={
                            contentCollectionFound.collection_info
                              ?.collection_id
                          }
                          timeout={2000}
                        >
                          {({ copied, copy }) => (
                            <Tooltip
                              label={copied ? "Copied" : "Copy"}
                              className={classesIcons.copy}
                              withArrow
                              position="right"
                            >
                              <ActionIcon
                                color={
                                  copied
                                    ? theme.customColors.actionIconFirst
                                    : theme.customColors.actionIconSecond
                                }
                                variant="subtle"
                                onClick={copy}
                              >
                                {copied ? (
                                  <IconCheck className={classesIcons.width} />
                                ) : (
                                  <IconCopy className={classesIcons.width} />
                                )}
                              </ActionIcon>
                            </Tooltip>
                          )}
                        </CopyButton>
                      </Text>
                    </Flex>
                    <Flex
                      gap={theme.flex.gap.xxs}
                      justify={theme.flex.justify.between}
                      className="item"
                      align={theme.flex.align.end}
                    >
                      <Text className="key">Blockchain Address</Text>
                      <Box className="dots"></Box>
                      <Text className="value">
                        <CustomAnchor
                          text={formatHexStr(
                            contentCollectionFound.collection_info
                              ?.user_address,
                          )}
                          componentClasses="link"
                          color={theme.customColors.black}
                          href={`/verify/user-data/?user=${contentCollectionFound.collection_info?.user_address}#collections`}
                        ></CustomAnchor>
                        <CopyButton
                          value={
                            contentCollectionFound.collection_info?.user_address
                          }
                          timeout={2000}
                        >
                          {({ copied, copy }) => (
                            <Tooltip
                              label={copied ? "Copied" : "Copy"}
                              className={classesIcons.copy}
                              withArrow
                              position="right"
                            >
                              <ActionIcon
                                color={
                                  copied
                                    ? theme.customColors.actionIconFirst
                                    : theme.customColors.actionIconSecond
                                }
                                variant="subtle"
                                onClick={copy}
                              >
                                {copied ? (
                                  <IconCheck className={classesIcons.width} />
                                ) : (
                                  <IconCopy className={classesIcons.width} />
                                )}
                              </ActionIcon>
                            </Tooltip>
                          )}
                        </CopyButton>
                      </Text>
                    </Flex>

                    {firstStampInCollection && (
                      <Flex
                        gap={theme.flex.gap.xxs}
                        justify={theme.flex.justify.between}
                        className="item"
                        align={theme.flex.align.end}
                      >
                        <Text className="key">Time of First Stamp</Text>
                        <Box className="dots"></Box>
                        <Text className="value value-without-copy">
                          {formatTimeStamp(
                            firstStampInCollection,
                            timeZone,
                            LONG_DISPLAY_FORMAT,
                          )}{" "}
                          ({timeZone})
                        </Text>
                      </Flex>
                    )}

                    {lastStampInCollection && (
                      <Flex
                        gap={theme.flex.gap.xxs}
                        justify={theme.flex.justify.between}
                        className="item"
                        align={theme.flex.align.end}
                      >
                        <Text className="key">Time of Last Stamp</Text>
                        <Box className="dots"></Box>
                        <Text className="value value-without-copy">
                          {formatTimeStamp(
                            lastStampInCollection,
                            timeZone,
                            LONG_DISPLAY_FORMAT,
                          )}{" "}
                          ({timeZone})
                        </Text>
                      </Flex>
                    )}

                    {totalStampInCollection && (
                      <Flex
                        gap={theme.flex.gap.xxs}
                        justify={theme.flex.justify.between}
                        className="item"
                        align={theme.flex.align.end}
                      >
                        <Text className="key">Total Stamps</Text>
                        <Box className="dots"></Box>
                        <Text className="value value-without-copy">
                          {totalStampInCollection}
                        </Text>
                      </Flex>
                    )}

                    {totalCollections && (
                      <Flex
                        gap={theme.flex.gap.xxs}
                        justify={theme.flex.justify.between}
                        className="item"
                        align={theme.flex.align.end}
                      >
                        <Text className="key">Total Collections for User</Text>
                        <Box className="dots"></Box>
                        <Text className="value value-without-copy">
                          {totalCollections}
                        </Text>
                      </Flex>
                    )}
                  </Box>

                  {resultMaxTimeStampDiff !== "0s" &&
                    contentCollectionFound.missing_in_blockchain.length === 0 &&
                    contentCollectionFound.missing_in_csv.length === 0 && (
                      <Box pt={theme.padding.xlEm}>
                        <Flex gap={theme.flex.gap.xs}>
                          <Text className="title">Timestamp Differences</Text>
                          <CustomTooltip
                            content={
                              <>
                                <Text
                                  classNames={{
                                    root: classes.tooltipText,
                                  }}
                                  pb={theme.padding.smMd}
                                >
                                  The table below shows how many of the
                                  Submitted Data records differed from the
                                  blockchain-based timestamps and by how much.
                                </Text>
                                <Text
                                  classNames={{
                                    root: classes.tooltipText,
                                  }}
                                >
                                  For record-by-record specifics, see the Record
                                  Details section below.
                                </Text>
                              </>
                            }
                          ></CustomTooltip>
                        </Flex>
                        <Container
                          id="container-difference"
                          styles={(theme) => ({
                            root: {
                              backgroundColor: theme.customBackgrounds.white,
                              width: "100%",
                              paddingBottom: "1em",
                            },
                          })}
                          size="lg"
                          pl={theme.padding.none}
                          pr={theme.padding.none}
                          mt={theme.margin.mdSpacious}
                        >
                          <Box className={tableClasses.table}>
                            <MantineReactTable table={tableToleranceNew} />
                          </Box>
                          <Text
                            pt={theme.padding.smMd}
                            pb={theme.padding.smMd}
                            size="1em"
                            c={theme.customColors.red}
                            styles={(theme) => ({
                              root: {
                                fontFamily: theme.headings.fontFamily.bold,
                              },
                            })}
                          >
                            Maximum Timestamp Deviation:{" "}
                            {resultMaxTimeStampDiff}
                          </Text>
                        </Container>
                      </Box>
                    )}
                  <Box pt={theme.padding.xlEm}>
                    <Flex gap={theme.flex.gap.xs}>
                      <Text className="title">
                        Record Details
                        {contentCollectionFound.missing_in_blockchain.length ===
                          0 &&
                          contentCollectionFound.missing_in_csv.length ===
                            0 && (
                            <>
                              {" "}
                              ({contentCollectionFound?.matched_records.length})
                            </>
                          )}
                      </Text>
                    </Flex>
                    <Container
                      styles={(theme) => ({
                        root: {
                          backgroundColor: theme.customBackgrounds.white,
                          width: "100%",
                          paddingBottom: "1em",
                        },
                      })}
                      size="lg"
                      pl={theme.padding.none}
                      pr={theme.padding.none}
                      mt={theme.margin.mdSpacious}
                    >
                      <Box pt={theme.padding.none} pb={theme.padding.smMd}>
                        <Flex
                          gap={theme.flex.gap.xs}
                          align={theme.flex.align.center}
                          justify={theme.flex.justify.between}
                        >
                          {contentCollectionFound.missing_in_blockchain
                            .length === 0 &&
                            contentCollectionFound.missing_in_csv.length ===
                              0 && (
                              <Flex
                                align={theme.flex.align.center}
                                justify={theme.flex.justify.between}
                                w={tabletMediaQuery ? "auto" : "50%"}
                              >
                                <Anchor
                                  underline="always"
                                  c={theme.customColors.blackLight}
                                  className="download-collection-records"
                                  styles={{
                                    root: {
                                      cursor: "pointer",
                                    },
                                  }}
                                  onClick={() =>
                                    downloadJsonToCsvRecords(
                                      [
                                        ...(Array.isArray(combinedData)
                                          ? combinedData
                                          : []),
                                      ],
                                      `CollectionRecords_${fileName}_${formatHexStr(
                                        contentCollectionFound.collection_info
                                          ?.collection_id,
                                        0,
                                        4,
                                        21,
                                        true,
                                      )}.csv`,
                                      timeZone,
                                      fileType,
                                    )
                                  }
                                >
                                  Download Record Details CSV
                                </Anchor>
                              </Flex>
                            )}
                        </Flex>
                      </Box>
                      <Box>
                        {(contentCollectionFound?.missing_in_csv.length > 0 ||
                          contentCollectionFound?.missing_in_blockchain.length >
                            0) && (
                          <Box pb={theme.padding.lg}>
                            <Anchor
                              underline="always"
                              c={theme.customColors.blackLight}
                              className="download-archive-records"
                              styles={{
                                root: {
                                  cursor: "pointer",
                                  paddingBottom: "1.75em",
                                },
                              }}
                              onClick={() =>
                                downloadJsonToCsvMissing(
                                  contentCollectionFound.original_csv_records,
                                  true,
                                  "archive_records.csv",
                                )
                              }
                            >
                              Download Archive Records
                            </Anchor>
                          </Box>
                        )}

                        {(contentCollectionFound?.missing_in_csv.length > 0 ||
                          contentCollectionFound?.missing_in_blockchain.length >
                            0) && (
                          <Box>
                            <Anchor
                              underline="always"
                              c={theme.customColors.blackLight}
                              className="download-collection-records"
                              styles={{
                                root: {
                                  cursor: "pointer",
                                  paddingBottom: "1.75em",
                                },
                              }}
                              onClick={() =>
                                downloadJsonToCsvMissing(
                                  contentCollectionFound.original_commitment_receipts,
                                  false,
                                  "collection_records.csv",
                                )
                              }
                            >
                              Download Collection Records
                            </Anchor>
                          </Box>
                        )}

                        {contentCollectionFound?.matched_records.length > 0 &&
                          contentCollectionFound?.missing_in_csv.length === 0 &&
                          contentCollectionFound?.missing_in_blockchain
                            .length === 0 && (
                            <Box className={tableClasses.table}>
                              <MantineReactTable
                                mb={theme.margin.mdSpacious}
                                table={table}
                              />
                            </Box>
                          )}
                      </Box>
                    </Container>
                  </Box>
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          </Box>
        </Container>
      </Flex>
    </>
  );
};

CollectionFound.propTypes = {
  staticBasePath: PropTypes.string.isRequired,
  fileName: PropTypes.string.isRequired,
  contentCollectionFound: PropTypes.object,
  fileType: PropTypes.string.isRequired,
};
export default CollectionFound;
